import * as React from 'react';

import type { BlockIfData } from '@gitbook/doc-core';
import type { BlockProps } from '@gitbook/slate-react';
import { Icon } from '@gitbook/spine-icons';
import {
    Button,
    Popover,
    PopoverRoot,
    PopoverTrigger,
    usePopoverTriggerState,
} from '@gitbook/spine-react';

import { useEvaluateExpression } from '../../Expressions';
import { ExpressionPopoverEditor } from '../adaptive';
import { BlockWrapper } from '../common';
import { useIsBlockHovered } from '../common/layout/hovered';
import { ReferenceBlockActions, ReferenceBlockFrame } from '../ReferenceBlockFrame';

/**
 * A block that conditionally renders its children based on an expression.
 */
export function BlockIf(props: BlockProps) {
    const { node, editor, isFocused, isSelected, children } = props;
    const trigger = usePopoverTriggerState();

    const onEditCondition = React.useCallback(() => {
        editor.change((change) => {
            change.collapseToStartOf(node);
            change.blur();
        });
        trigger.open();
    }, [editor, node, trigger]);

    const { expression } = node.data.toFrozenObject() as BlockIfData;
    const readOnly = editor.readOnly;

    return (
        <BlockWrapper {...props}>
            <PopoverRoot {...trigger.rootProps}>
                <BlockIfContentFrame
                    nodeKey={node.key}
                    isFocused={isFocused || isSelected}
                    condition={expression}
                    onEditCondition={onEditCondition}
                    readOnly={readOnly}
                >
                    {children}
                </BlockIfContentFrame>

                <Popover placement="bottom-end" className="p-2">
                    <ExpressionPopoverEditor
                        node={node}
                        editor={editor}
                        title={{
                            editable: 'Edit condition',
                            readOnly: 'Only show this content if',
                        }}
                        documentationURL={{ path: 'creating-content/blocks/conditional-content' }}
                    />
                </Popover>
            </PopoverRoot>
        </BlockWrapper>
    );
}

function BlockIfContentFrame(props: {
    nodeKey: string;
    isFocused: boolean;
    condition: string;
    onEditCondition: () => void;
    readOnly: boolean;
    children: React.ReactNode;
}) {
    const { nodeKey, isFocused, condition, onEditCondition, readOnly, children } = props;
    const isBlockHovered = useIsBlockHovered(nodeKey);

    const result = useEvaluateExpression(condition);

    return (
        <div className="py-2">
            <ReferenceBlockFrame isFocused={isFocused} isHovered={isBlockHovered}>
                <ReferenceBlockActions>
                    <PopoverTrigger>
                        <Button
                            size="small"
                            kind={result.error ? 'danger' : 'primary'}
                            leadingIcon={Icon.Condition}
                            label={condition || 'Condition'}
                            title={
                                readOnly
                                    ? 'View the conditional expression'
                                    : 'Edit the conditional expression'
                            }
                            onPress={onEditCondition}
                        />
                    </PopoverTrigger>
                </ReferenceBlockActions>
            </ReferenceBlockFrame>
            {children}
        </div>
    );
}
